import { api } from './api';

/**
 * The Settings manifest — what the signed-in person's Settings actually contains.
 *
 * Built on the server from the registry and the resolved context (docs/SETTINGS-MODEL.md §3).
 * The shell RENDERS this; it never decides for itself which sections a barrister sees or whether
 * a field is editable. There is no role logic on this side of the wire — deliberately, because a
 * second implementation would drift and a drifted permission is a lie.
 */

/** Why a readable setting cannot be edited. Decided by the server; worded by us. */
export type ReadOnlyReason =
  | 'organisation-managed'
  | 'granted-by-relationship'
  | 'terms-set-by-chambers'
  | 'not-editable';

export type SettingScope = 'PERSONAL' | 'ORGANISATION' | 'RELATIONSHIP';

export interface ManifestSetting {
  key: string;
  scope: SettingScope;
  /** 'partial' = stored and returned, but not yet acted on by the surfaces it names. */
  status: 'live' | 'partial' | 'missing';
  canWrite: boolean;
  readOnlyReason: ReadOnlyReason | null;
}

export interface ManifestSection {
  id: string;
  scope: SettingScope;
  settings: ManifestSetting[];
  hasWritable: boolean;
}

export interface SettingsManifest {
  context: {
    memberType: string;
    workspaceType: 'solo' | 'chambers';
    actor: 'barrister' | 'clerk' | 'admin' | 'staff' | 'external';
    isOwner: boolean;
    capabilities: Record<string, string>;
    supportsBarristerCount: number;
  };
  sections: ManifestSection[];
}

export async function fetchSettingsManifest(): Promise<SettingsManifest> {
  const res = await api.get('/me/settings-manifest');
  return (res.data as { data: SettingsManifest }).data;
}

/** Your own role and granted capabilities. Never anyone else's — the endpoint takes no id. */
export interface MyAccess {
  memberType: string;
  workspaceType: 'solo' | 'chambers';
  isOwner: boolean;
  roles: Array<{ key: string; name: string; description: string | null; isSystem: boolean; grantedAt: string }>;
  capabilities: Record<string, string>;
  supportsBarristerCount: number;
}

export async function fetchMyAccess(): Promise<MyAccess> {
  const res = await api.get('/me/access');
  return (res.data as { data: MyAccess }).data;
}
